import { ReactElement, useMemo, useCallback, useState } from 'react'
import {
  Box,
  Card,
  Typography,
  Chip,
  Alert,
  Skeleton,
  Button,
  IconButton,
} from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import {
  DataGridPremium,
  GridColDef,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'

import {
  getZodValidationError,
  shouldShowZodFieldError,
} from 'src/v3/utilities/validation'
import { dataGridSx } from '../../styles'
import {
  courseColumns,
  DATAGRID_DEFAULT_PAGE_SIZE,
  DATAGRID_PAGE_SIZE_OPTIONS,
} from '../../constants'
import type { CourseRow } from '../utils/course-transforms'
import DataGridCourseFilters from '../../components/data-grid-course-filters'
import CourseDetailModal from '../../components/course-detail-modal'

interface FormFieldState {
  state: {
    meta: any
  }
}

interface CoursesDataGridProps {
  courseRows: CourseRow[]
  selectedCourses: GridRowSelectionModel
  onSelectionChange: (newSelection: GridRowSelectionModel) => void
  searchQuery: string
  categoryFilter: string
  availableCategories: string[]
  onSearchChange: (value: string) => void
  onCategoryChange: (value: string) => void
  onResetFilters: () => void
  isLoading?: boolean
  field: FormFieldState
  editing?: boolean
  title?: string
}

export default function CoursesDataGrid({
  courseRows,
  selectedCourses,
  onSelectionChange,
  searchQuery,
  categoryFilter,
  availableCategories,
  onSearchChange,
  onCategoryChange,
  onResetFilters,
  isLoading = false,
  field,
  editing = true,
  title = 'Select Courses',
}: CoursesDataGridProps): ReactElement {
  let [detailCourseID, setDetailCourseID] = useState<string | null>(null)
  let detailCourseName = useMemo(
    () => courseRows.find((c) => c.id === detailCourseID)?.name ?? '',
    [courseRows, detailCourseID],
  )

  let infoColumn = useMemo<GridColDef>(
    () => ({
      field: 'info',
      headerName: '',
      width: 48,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          aria-label="View course details"
          onClick={(e) => {
            e.stopPropagation()
            setDetailCourseID(params.row.id)
          }}
        >
          <InfoOutlined fontSize="small" />
        </IconButton>
      ),
    }),
    [],
  )

  let CoursesToolbar = useCallback(
    () => <DataGridToolbar fileName="training-series-courses" />,
    [],
  )

  // Calculate statistics for selected courses
  let stats = useMemo(() => {
    let selectedCourseRows =
      selectedCourses.type === 'exclude'
        ? courseRows.filter((c) => !selectedCourses.ids.has(c.id))
        : courseRows.filter((c) => selectedCourses.ids.has(c.id))

    let totalHours = selectedCourseRows.reduce(
      (sum, course) => sum + course.length,
      0,
    )
    let categories = Array.from(
      new Set(selectedCourseRows.map((c) => c.category)),
    )

    return {
      courseCount: selectedCourseRows.length,
      totalHours,
      categories,
    }
  }, [courseRows, selectedCourses])

  return (
    <Card className="p-6 mb-6">
      <Box className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <Typography variant="h6" component="h2">
          {title}
        </Typography>
        <Box className="flex items-center gap-2">
          <Chip
            label={`${stats.courseCount} course${stats.courseCount !== 1 ? 's' : ''}`}
            size="medium"
            color="primary"
          />
          <Chip
            label={`${stats.totalHours.toFixed(1)} hours`}
            size="medium"
            color="primary"
          />
          {(searchQuery || categoryFilter) && (
            <Button
              className="ml-2 whitespace-nowrap"
              variant="outlined"
              color="secondary"
              onClick={onResetFilters}
              size="medium"
              sx={{ height: 32 }}
              aria-label="Reset filters"
            >
              Reset Filters
            </Button>
          )}
        </Box>
      </Box>

      {editing && (
        <DataGridCourseFilters
          className="mb-4"
          searchQuery={searchQuery}
          categoryFilter={categoryFilter}
          availableCategories={availableCategories}
          onSearchChange={onSearchChange}
          onCategoryChange={onCategoryChange}
        />
      )}

      {shouldShowZodFieldError(field.state.meta) ? (
        <Alert severity="error" sx={{ mb: 2 }} role="alert">
          {getZodValidationError(field.state.meta.errors)}
        </Alert>
      ) : null}

      {isLoading ? (
        <Skeleton
          variant="rectangular"
          height={400}
          sx={{ borderRadius: 1 }}
          aria-label="Loading courses"
        />
      ) : (
        <DataGridPremium
          rows={courseRows}
          columns={[...courseColumns, infoColumn]}
          slots={{ toolbar: CoursesToolbar }}
          showToolbar
          checkboxSelection={editing}
          disableRowSelectionOnClick
          rowSelectionModel={
            editing ? selectedCourses : { type: 'include', ids: new Set() }
          }
          onRowSelectionModelChange={onSelectionChange}
          initialState={{
            pagination: {
              paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE },
            },
          }}
          pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
          sx={dataGridSx}
          aria-label={
            editing
              ? 'Select courses for training series'
              : 'Training series courses'
          }
        />
      )}
      <CourseDetailModal
        courseID={detailCourseID}
        courseName={detailCourseName}
        onClose={() => setDetailCourseID(null)}
      />
    </Card>
  )
}
